'use client'

/**
 * MobileWorkspaceDrawer — bottom sheet with two-level drill nav.
 *
 * Level 1 (root): full-width ghost button (workspace avatar + ring + name)
 *   that drills to Level 2 when tapped, plus the Manage and (admin-only)
 *   Atrium nav groups mirroring the desktop sidebar. Tapping any nav item
 *   navigates and closes the drawer.
 *
 * Level 2 (workspace switcher): same content as the desktop popover —
 *   workspace card with Settings + Invite teammates buttons, impressions
 *   progress bar, all-workspaces list, "Create new workspace" footer.
 *   A back chevron in the top-left returns to Level 1.
 *
 * Drill animation: both views are rendered side-by-side inside a wrapper
 * that translates -100% on level === 'switcher'. iOS-feeling slide.
 *
 * State reset: when the drawer closes, level resets to 'root' so the next
 * open lands on the first view again.
 *
 * Settings & "create workspace" handlers come from props so the parent
 * (LobbyLayoutClient) can mount the existing WorkspaceSettingsPanel and
 * WorkspaceForm dialogs once and share them across desktop + mobile.
 */

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { WorkspaceCreditsAvatar } from '@/components/workspace/WorkspaceCreditsAvatar'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useWorkspaceCredits } from '@/hooks/useWorkspaceCredits'
import { useCachedUser } from '@/hooks/useCachedUser'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  TokenCircleIcon as TokenCircleStrokeIcon,
  PlugSocketIcon as PlugSocketStrokeIcon,
  FlashIcon as FlashStrokeIcon,
  PlusSignIcon as PlusSignStrokeIcon,
  Tick02Icon,
  UserAdd01Icon,
  DashboardSpeed01Icon as DashboardSpeed01StrokeIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import {
  TokenCircleIcon as TokenCircleSolidIcon,
  PlugSocketIcon as PlugSocketSolidIcon,
  FlashIcon as FlashSolidIcon,
  DashboardSpeed01Icon as DashboardSpeed01SolidIcon,
  Settings01Icon,
} from '@hugeicons-pro/core-solid-rounded'
import { cn } from '@/lib/utils'

const WORKSPACE_AVATAR_BASE = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/workspace-avatars`

type DrawerLevel = 'root' | 'switcher'

type ManageNavItem = {
  href: string
  label: string
  strokeIcon: typeof TokenCircleStrokeIcon
  solidIcon: typeof TokenCircleSolidIcon
}

const MANAGE_NAV: ManageNavItem[] = [
  { href: '/usage', label: 'Usage', strokeIcon: TokenCircleStrokeIcon, solidIcon: TokenCircleSolidIcon },
  { href: '/connectors', label: 'Connectors', strokeIcon: PlugSocketStrokeIcon, solidIcon: PlugSocketSolidIcon },
  { href: '/activity', label: 'Activity', strokeIcon: FlashStrokeIcon, solidIcon: FlashSolidIcon },
]

const ATRIUM_NAV: ManageNavItem[] = [
  { href: '/admin', label: 'Admin', strokeIcon: DashboardSpeed01StrokeIcon, solidIcon: DashboardSpeed01SolidIcon },
]

function formatPlanLabel(tier: string | null | undefined) {
  if (!tier) return 'Plan'
  return tier
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function formatCredits(value: number) {
  return value.toLocaleString()
}

export type MobileWorkspaceDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Open the workspace settings sheet (hoisted to the lobby layout). */
  onOpenWorkspaceSettings: (section?: string) => void
  /** Open the "create new workspace" form (hoisted to the lobby layout). */
  onOpenCreateWorkspace: () => void
}

export function MobileWorkspaceDrawer({
  open,
  onOpenChange,
  onOpenWorkspaceSettings,
  onOpenCreateWorkspace,
}: MobileWorkspaceDrawerProps) {
  const router = useRouter()
  const [level, setLevel] = useState<DrawerLevel>('root')
  const { workspace, workspaces, workspaceRoles, isLoading: workspacesLoading, switchWorkspace } = useWorkspace()
  const credits = useWorkspaceCredits(workspace?.id ?? null)
  const { user, isLoggedIn } = useCachedUser()
  const showAdminNav = !!user?.email?.endsWith('@atrium.run')

  // Reset to the root view whenever the drawer closes so the next open is fresh.
  useEffect(() => {
    if (!open) {
      // Defer to the close animation finishing before resetting visible level
      // (avoids the user seeing the slide reset mid-fade).
      const id = window.setTimeout(() => setLevel('root'), 350)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [open])

  const close = () => onOpenChange(false)
  const navigateAndClose = (href: string) => {
    close()
    router.push(href)
  }
  const handleWorkspaceSwitch = (id: string) => {
    switchWorkspace(id)
    close()
  }
  const goSettings = (section?: string) => {
    close()
    onOpenWorkspaceSettings(section)
  }
  const goCreate = () => {
    close()
    onOpenCreateWorkspace()
  }
  const goUsage = () => {
    close()
    router.push('/usage')
  }

  return (
    // Vaul Drawer gives us drag-to-dismiss for free (matches the user profile
    // sheet's behavior). The default vaul handle bar in DrawerContent is hidden
    // (`hidden` class on its `mt-4 hidden` rule), so we render our own visible
    // handle just below.
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={cn(
          'h-auto max-h-[85dvh] gap-0 rounded-t-2xl border-t-0 bg-atrium-background p-0',
          'pb-[env(safe-area-inset-bottom)]',
        )}
      >
        <DrawerTitle className="sr-only">Workspace menu</DrawerTitle>
        <DrawerDescription className="sr-only">
          Access workspace switcher, settings, usage, connectors, and activity.
        </DrawerDescription>

        {/* Visible drag handle. Vaul accepts drag gestures anywhere on the
            drawer body; the handle just signals draggability. */}
        <div className="flex shrink-0 justify-center pb-1 pt-2.5">
          <div className="h-1 w-9 rounded-full bg-atrium-border" aria-hidden />
        </div>

        {/* Slide container — both views always rendered, parent translates between them. */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div
            className={cn(
              'flex h-full w-[200%] transition-transform duration-300 ease-out motion-reduce:transition-none',
              level === 'switcher' ? '-translate-x-1/2' : 'translate-x-0',
            )}
          >
            {/* Level 1: root */}
            <div className="w-1/2 shrink-0 overflow-y-auto px-3 pb-4">
              <RootView
                workspaceName={workspace?.display_name ?? 'Workspace'}
                avatarUrl={workspace?.avatar_path ? `${WORKSPACE_AVATAR_BASE}/${workspace.avatar_path}` : undefined}
                usagePercent={credits.usagePercent}
                impressionsRemaining={credits.remaining}
                impressionsTotal={credits.lifetimePurchased}
                onDrillToSwitcher={() => setLevel('switcher')}
                onNavigate={navigateAndClose}
                showAdminNav={showAdminNav}
                isLoggedIn={!!isLoggedIn}
                onClose={close}
              />
            </div>

            {/* Level 2: switcher */}
            <div className="w-1/2 shrink-0 overflow-y-auto px-3 pb-4">
              <SwitcherView
                onBack={() => setLevel('root')}
                workspace={workspace}
                workspaces={workspaces}
                workspaceRoles={workspaceRoles}
                workspacesLoading={workspacesLoading}
                onSwitch={handleWorkspaceSwitch}
                onSettings={goSettings}
                onCreate={goCreate}
                onViewUsage={goUsage}
                creditsRemaining={credits.remaining}
                lifetimePurchased={credits.lifetimePurchased}
                usagePercent={credits.usagePercent}
                onClose={close}
              />
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Level 1 — Root view
// ─────────────────────────────────────────────────────────────────────────────

function RootView({
  workspaceName,
  avatarUrl,
  usagePercent,
  impressionsRemaining,
  impressionsTotal,
  onDrillToSwitcher,
  onNavigate,
  showAdminNav,
  isLoggedIn,
  onClose,
}: {
  workspaceName: string
  avatarUrl: string | undefined
  usagePercent: number | null
  impressionsRemaining: number
  impressionsTotal: number
  onDrillToSwitcher: () => void
  onNavigate: (href: string) => void
  showAdminNav: boolean
  isLoggedIn: boolean
  onClose: () => void
}) {
  if (!isLoggedIn) {
    return (
      <div className="flex flex-col gap-4 px-1 pt-2 pb-2">
        {/* Guest hero */}
        <div className="rounded-2xl bg-accent-700 px-5 py-5 text-white">
          <p className="text-[17px] font-extrabold leading-snug tracking-tight">
            Plan, launch & manage<br />your campaigns with AI.
          </p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-white/80">
            Create a free account to get started.
          </p>
        </div>
        <div className="flex flex-col gap-2.5">
          <Link
            href="/signup"
            onClick={onClose}
            className="flex w-full items-center justify-center rounded-2xl bg-accent-600 px-5 py-[14px] text-[15px] font-bold text-white transition active:scale-[0.98]"
          >
            Create a free account
          </Link>
          <Link
            href="/login"
            onClick={onClose}
            className="flex w-full items-center justify-center rounded-2xl border-2 border-atrium-border bg-atrium-content px-5 py-[13px] text-[15px] font-semibold text-atrium-black transition active:scale-[0.98]"
          >
            Sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 pt-1">
      {/* Workspace ghost button — full width, drills to switcher */}
      <button
        type="button"
        onClick={onDrillToSwitcher}
        aria-label={`Switch workspace · current: ${workspaceName}`}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors',
          'bg-atrium-content/40 active:bg-atrium-content',
          'min-h-[56px]',
        )}
      >
        <WorkspaceCreditsAvatar
          imageSrc={avatarUrl}
          usagePercent={usagePercent}
          impressionsRemaining={impressionsRemaining}
          impressionsTotal={impressionsTotal}
          size={36}
          showHoverPercent={false}
        />
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-atrium-black">
          {workspaceName}
        </span>
        {/* Right-pointing chevron */}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 shrink-0 text-atrium-black/40" aria-hidden>
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>

      <NavGroup label="Manage">
        {MANAGE_NAV.map((item) => (
          <DrawerNavLink key={item.href} item={item} onNavigate={onNavigate} />
        ))}
      </NavGroup>

      {showAdminNav ? (
        <NavGroup label="Atrium">
          {ATRIUM_NAV.map((item) => (
            <DrawerNavLink key={item.href} item={item} onNavigate={onNavigate} />
          ))}
        </NavGroup>
      ) : null}
    </div>
  )
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-atrium-black/40">
        {label}
      </div>
      {children}
    </div>
  )
}

function DrawerNavLink({
  item,
  onNavigate,
}: {
  item: ManageNavItem
  onNavigate: (href: string) => void
}) {
  // Use the stroke icon by default; active highlighting in this drawer is minimal
  // because tapping closes immediately and routes — we just need clear tap targets.
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.href)}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-atrium-black transition-colors',
        'active:bg-atrium-content',
        'min-h-[44px]',
      )}
    >
      <HugeiconsIcon icon={item.strokeIcon} className="size-5 shrink-0 text-atrium-black/70" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Level 2 — Workspace switcher view
// ─────────────────────────────────────────────────────────────────────────────

type SwitcherWorkspace = {
  id: string
  display_name: string
  avatar_path: string | null
  plan_tier?: string | null
}

function SwitcherView({
  onBack,
  workspace,
  workspaces,
  workspaceRoles,
  workspacesLoading,
  onSwitch,
  onSettings,
  onCreate,
  onViewUsage,
  creditsRemaining,
  lifetimePurchased,
  usagePercent,
  onClose,
}: {
  onBack: () => void
  workspace: SwitcherWorkspace | null
  workspaces: SwitcherWorkspace[]
  workspaceRoles: Record<string, string>
  workspacesLoading: boolean
  onSwitch: (id: string) => void
  onSettings: (section?: string) => void
  onCreate: () => void
  onViewUsage: () => void
  creditsRemaining: number
  lifetimePurchased: number
  usagePercent: number | null
  onClose: () => void
}) {
  const isGuest = !workspace?.id && workspaces.length === 0
  const [showGuestAuth, setShowGuestAuth] = useState(false)

  const handleGuestAction = () => {
    if (isGuest) {
      setShowGuestAuth(true)
    }
  }

  return (
    <div className="flex flex-col gap-3 pt-1">
      {/* Inline guest auth prompt */}
      {showGuestAuth ? (
        <div className="rounded-2xl bg-accent-700 px-4 py-4 text-white">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[15px] font-extrabold">Sign in to continue</p>
            <button
              type="button"
              onClick={() => setShowGuestAuth(false)}
              aria-label="Dismiss"
              className="flex size-7 items-center justify-center rounded-full bg-white/20 text-white transition active:bg-white/30"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="size-4" aria-hidden><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <p className="mb-3 text-[13px] leading-relaxed text-white/80">
            Create a free account to access settings, invite teammates, and manage your workspace.
          </p>
          <div className="flex flex-col gap-2">
            <Link
              href="/signup"
              onClick={onClose}
              className="flex w-full items-center justify-center rounded-xl bg-white px-4 py-2.5 text-[13px] font-bold text-accent-700 transition active:scale-[0.98]"
            >
              Create a free account
            </Link>
            <Link
              href="/login"
              onClick={onClose}
              className="flex w-full items-center justify-center rounded-xl bg-white/20 px-4 py-2.5 text-[13px] font-semibold text-white transition active:scale-[0.98]"
            >
              Sign in
            </Link>
          </div>
        </div>
      ) : null}

      {/* Header row with back chevron */}
      <div className="flex items-center gap-1 px-1">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to workspace menu"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg text-atrium-black/70 transition-colors active:bg-atrium-content"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="flex-1 text-[15px] font-semibold text-atrium-black">Switch workspace</h2>
      </div>

      {/* Current workspace card */}
      {workspace ? (
        <div className="rounded-xl border border-atrium-border bg-atrium-background p-3">
          <div className="flex items-start gap-3">
            <Avatar className="size-10 shrink-0 overflow-hidden rounded-full">
              <AvatarImage
                src={
                  workspace.avatar_path
                    ? `${WORKSPACE_AVATAR_BASE}/${workspace.avatar_path}`
                    : undefined
                }
                className="object-cover"
              />
              <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
                {'\u200b'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-medium text-atrium-black">
                {workspace.display_name}
              </p>
              {isGuest ? (
                <p className="mt-0.5 text-xs text-atrium-black/55">Guest</p>
              ) : (
                <p className="mt-0.5 text-xs text-atrium-black/55">
                  {formatPlanLabel(workspace.plan_tier)}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'h-9 gap-1.5 border-atrium-border px-2 text-xs font-normal',
                isGuest && 'opacity-60',
              )}
              onClick={isGuest ? handleGuestAction : () => onSettings()}
            >
              <HugeiconsIcon icon={Settings01Icon} className="size-3.5 shrink-0" />
              Settings
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'h-9 gap-1.5 border-atrium-border px-2 text-xs font-normal',
                isGuest && 'opacity-60',
              )}
              onClick={isGuest ? handleGuestAction : () => onSettings('team')}
            >
              <HugeiconsIcon icon={UserAdd01Icon} className="size-3.5 shrink-0" />
              Invite teammates
            </Button>
          </div>
        </div>
      ) : null}

      {/* Impressions remaining */}
      {workspace ? (
        <div className="rounded-xl border border-atrium-border bg-atrium-background p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-atrium-black/45">
              Impressions remaining
            </p>
            <button
              type="button"
              className="text-xs font-medium text-accent-600 hover:text-accent-700"
              onClick={isGuest ? handleGuestAction : onViewUsage}
            >
              View usage
            </button>
          </div>
          {usagePercent !== null ? (
            <Progress
              value={usagePercent}
              className="h-2 bg-atrium-border/60 [&>div]:bg-accent-600"
              aria-label="Remaining credits"
            />
          ) : (
            <div className="h-2 w-full rounded-full bg-atrium-border/60" aria-hidden />
          )}
          <p className="mt-2 text-xs text-atrium-black/60">
            {formatCredits(creditsRemaining)} / {formatCredits(lifetimePurchased)} impressions
          </p>
        </div>
      ) : null}

      {/* All workspaces list */}
      {!isGuest ? (
        <div className="flex flex-col gap-1">
          <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-atrium-black/45">
            All workspaces
          </p>
          {workspacesLoading ? (
            <div className="flex flex-col gap-1 px-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg px-2 py-2">
                  <Skeleton className="size-8 rounded-full bg-atrium-border/60" />
                  <Skeleton className="h-4 flex-1 bg-atrium-border/60" />
                </div>
              ))}
            </div>
          ) : (
            workspaces.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => onSwitch(org.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                  'active:bg-atrium-content',
                  workspace?.id === org.id && 'bg-atrium-content/60',
                  'min-h-[52px]',
                )}
              >
                <Avatar className="size-8 shrink-0 overflow-hidden rounded-full">
                  <AvatarImage
                    src={org.avatar_path ? `${WORKSPACE_AVATAR_BASE}/${org.avatar_path}` : undefined}
                    className="object-cover"
                  />
                  <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
                    {'\u200b'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-atrium-black">
                    {org.display_name}
                  </span>
                  {workspaceRoles[org.id] ? (
                    <span className="block text-[11px] text-atrium-black/45">
                      {{ owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' }[
                        workspaceRoles[org.id]
                      ] || workspaceRoles[org.id]}
                    </span>
                  ) : null}
                </div>
                {workspace?.id === org.id ? (
                  <HugeiconsIcon icon={Tick02Icon} className="size-4 shrink-0 text-accent-600" />
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}

      {/* Create new workspace */}
      <div className="border-t border-atrium-border pt-2">
        <button
          type="button"
          onClick={isGuest ? handleGuestAction : onCreate}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-[14px] font-medium text-atrium-black transition-colors',
            'active:bg-atrium-content',
            isGuest && 'opacity-60',
            'min-h-[48px]',
          )}
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-black/10">
            <HugeiconsIcon icon={PlusSignStrokeIcon} className="size-3.5" />
          </span>
          Create new workspace
        </button>
      </div>
    </div>
  )
}
